import React, { useCallback, useState } from 'react';
import { Query } from '../_common';
import SearchBar from '../SearchBar';
import { SearchTypeEnum } from '../../types/SearchTypeEnum';
import { useAuthContext } from '../Auth/AuthProvider';
import { useStoreContext } from '../App/StoreProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import { GET_ALL_REGIONS, GET_ALL_STATES, GET_FILTERED_YARDS, GET_YARD_TYPES } from '../../graphql/client/queries';
import { Error, Loading } from '../_common';
import { useMutation, useQuery } from '@apollo/client/react';
import './Yards.scss';
import {
    GetFilteredYards,
    GetFilteredYards_getFilteredYards as Yard,
    GetYardTypes_getYardTypes as YardTypes,
    SaveYard,
    GetAllStates,
    GetAllStates_getAllStates as StateType,
    GetAllRegions,
    GetAllRegions_getAllRegions as RegionType,
    GetYardTypes,
} from '../../types/schemaTypes';
import { ToastContainer, toast } from 'react-toastify';
import { toastOpt } from '../../utils/helpers/toastHelper'
import { DELETE_YARD, SAVE_YARD } from '../../graphql/client/mutations';
import AddEditYardsModal from './AddEditYardsModal';
import { Option } from '../Users/interface';
import { ModalForm } from './interface';
import YardList from './YardList';

interface LatLng {
    lat: number;
    lng: number;
}

const Messages = {
    create: 'Yard created successfully',
    edit: 'Yard updated successfully',
    delete: 'Yard deleted successfully',
};
const ErrorMessages = {
    create: 'Failed to create Yard',
    edit: 'Failed to edit Yard',
    delete: 'Failed to delete Yard',
};

const PAGE_SIZE = 50;

const Yards = (): React.ReactElement => {
    const authContext = useAuthContext();
    const isViewYardsEnabled = authContext.userHasPermission(PermissionEnum.ViewYard);
    const canEditYard = useAuthContext().userHasPermission(PermissionEnum.EditYard);
    const canDeleteYard = useAuthContext().userHasPermission(PermissionEnum.DeleteYard);
    document.title = 'SBS';

    const [state] = useStoreContext();
    const {
        yardList: { searchPhrase },
    } = state;

    const variables = {
        input: searchPhrase,
    };

    const [selectedYard, setSelectedYard] = useState<Yard>();
    const [showAddYardModal, setShowAddYardModal] = useState(false);
    const [modalForm, setModalForm] = useState(ModalForm.EMPTY);
    const [errorMessage, setErrorMessage] = useState('');
    const [isEndOfResults, setIsEndOfResults] = useState(false);

    const onCompleted = action => {
        toast.success(Messages[action], toastOpt);
    };
    const onActionError = action => {
        toast.error(ErrorMessages[action], toastOpt);
    };

    const { data: statesData } = useQuery<GetAllStates>(GET_ALL_STATES);
    const options: Option[] = [];
    if (statesData && statesData.getAllStates) {
        statesData.getAllStates.forEach((element: StateType | null) => {
            if (!element || element.stateName === null || element.id === null) return;
            options.push({ id: element.id, name: element.stateName });
        });
    }

    const { data: regionsData } = useQuery<GetAllRegions>(GET_ALL_REGIONS);
    const regions: Option[] = [];
    let regionsWithDistricts: RegionType[] = [];
    if (regionsData && regionsData.getAllRegions) {
        regionsWithDistricts = regionsData.getAllRegions.filter((r): r is RegionType => r != null);
        regionsData.getAllRegions.forEach((element: RegionType | null) => {
            if (!element || element.regionName === null) return;
            regions.push({ id: element.id, name: element.regionName });
        });
    }

    const { data: yardTypesData } = useQuery<GetYardTypes>(GET_YARD_TYPES);
    const yardTypes: Option[] = [];
    if (yardTypesData && yardTypesData.getYardTypes) {
        yardTypesData.getYardTypes.forEach((element: YardTypes | null) => {
            if (!element || element.descrip === null || element.id === null) return;
            yardTypes.push({ id: element.id, name: element.descrip });
        });
    }

    const [deleteYard] = useMutation(DELETE_YARD, {
        onCompleted: () => onCompleted('delete'),
        onError: () => onActionError('delete'),
        refetchQueries: () => {
            return [
                {
                    query: GET_FILTERED_YARDS,
                    variables: { variables },
                },
            ];
        },
    });

    const handleOnHide = (): void => {
        setSelectedYard(undefined);
        setErrorMessage('');
        setShowAddYardModal(false);
    };

    const onCompletedYard = (data: SaveYard): void => {
        if (data.saveYard === null) return;
        const saveLofaErrors = data.saveYard.errors;
        if (saveLofaErrors && saveLofaErrors.length > 0) {
            setErrorMessage(saveLofaErrors[0] || '');
        } else {
            setSelectedYard(undefined);
            setShowAddYardModal(false);
            setErrorMessage('');
        }
    };

    const [saveYard, { loading: saveLoading }] = useMutation<SaveYard>(SAVE_YARD, {
        onCompleted: onCompletedYard,
        onError: (err) => setErrorMessage(err.message || ''),
        refetchQueries: [
            {
                query: GET_FILTERED_YARDS,
                variables: { variables },
            },
        ]
    });

    const onDelete = useCallback(async (record: any) => {
        const variables = { input: { id: record.id } };
        await deleteYard({ variables });
    }, []);

    const handleOnSave = async (
        id: number,
        yardName: string,
        yardTypeId: number,
        latitude: number,
        longitude: number,
        addressStreet: string,
        city: string,
        stateId: number,
        regionId: number,
        districtId: number,
        utm: string,
        zip: string,
        phoneNumber: string,
        directions: string,
        description: string,
        geoFenceType: string,
        geoFenceRadius: number,
        yardPolygonCoordinates: LatLng[],
    ): Promise<void> => {
        const variables = {
            input: {
                id,
                yardName,
                yardTypeId,
                latitude,
                longitude,
                addressStreet,
                city,
                stateId,
                regionId,
                districtId,
                utm,
                zip,
                phoneNumber,
                directions,
                description,
                geoFenceRadius,
                geoFenceType,
                yardPolygonCoordinates,
            },
        };
        if (!selectedYard)
            setSelectedYard({
                __typename: "GetFilteredYardsResponse",
                yardName:yardName,
                id: id,
                latitude: latitude,
                longitude: longitude,
                yardType: yardTypeId.toString(),
                yardTypeId:yardTypeId,
                addressStreet: addressStreet,
                city: city,
                zip: zip,
                phoneNumber: phoneNumber,
                description: description,
                stateId: stateId,
                regionId: regionId,
                districtId: districtId,
                utm: utm,
                directions: directions,
                geoFenceRadius: geoFenceRadius,
                geoFenceType: geoFenceType,
                yardPolygonCoordinates: yardPolygonCoordinates.map(polygonCoordinate => {
                    return {
                        __typename: "polygnCoordinates",
                        lat: polygonCoordinate.lat,
                        lng: polygonCoordinate.lng,
                    }
                }),
            });
        await saveYard({ variables });
    };

    const openGoogleMaps = (lat: number, lng: number, lofaName: string): void => {
        const url = 'https://maps.google.com/?q=' + lat + ',' + lng;
        const newTab = window.open(url);
        setTimeout(function (): void {
            if (newTab) {
                newTab.document.title = lofaName!;
            }
        }, 100);
    };

    document.title = 'Yards';

    if (!isViewYardsEnabled) {
        return (
            <div className="NoResult-Container Yards-Unauth-Container">
                <div className="NoResult-First-Title-Secondary">Unauthorized</div>
                <div className="NoResult-First-Title-Primary">You do not have permission to view this screen</div>
            </div>
        );
    }

    return (
        <Query<GetFilteredYards> query={GET_FILTERED_YARDS} variables={variables}>
            {({
                error,
                data,
                loading,
                fetchMore,
            }): React.ReactElement | null => {

                const offset = data && data.getFilteredYards ? data.getFilteredYards.length / PAGE_SIZE : 0;

                React.useEffect(() => {
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults && data && data.getFilteredYards && data.getFilteredYards.length < PAGE_SIZE)
                    ) {
                        setIsEndOfResults(true);
                    }

                }, [offset, data?.getFilteredYards?.length,])


                if (error) {
                    return (
                        <div className="NoResult-Container">
                            <Error error={error} />;
                        </div>
                    );
                }

                const yards: Yard[] = [];

                if (data && data.getFilteredYards && data.getFilteredYards.length !== 0) {
                    data.getFilteredYards.forEach((element) => {
                        if (element !== null) yards.push(element as Yard);
                    });
                }

                return (
                    <div className="Yards-Container">
                        <div className="Yards-Top-Container">
                            <SearchBar type={SearchTypeEnum.Yards} />
                            <div className="Yards-RightOptions">
                                {canEditYard && (
                                    <div
                                        className="Yards-Create-New Row"
                                        onClick={() => {
                                            setShowAddYardModal(true);
                                            setModalForm(ModalForm.ADD_YARD);
                                        }}
                                    >
                                        <i className="material-icons Yards-Icon">add_circle</i>
                                        <span className="Yards-Link">Create new Yard</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <div className="NoResult-Container">
                                <Loading />
                            </div>
                        ) : yards.length === 0 && searchPhrase.length === 0 ? (
                            <div className="NoResult-Container">
                                <div className="NoResult-First-Title-Primary">No Yard details available.</div>
                            </div>
                        ) : yards.length === 0 ? (
                            <div className="NoResult-Container">
                                <div className="NoResult-First-Title-Primary">No Yard details found.</div>
                            </div>
                        ) : (
                            <div>
                                <div className="Yards-Bottom-Container">
                                    <ToastContainer />
                                    <div className="Yards-Header-Title-Lg">Yard Name</div>
                                    <div className="Yards-Header-Title-Lg">Yard Type</div>
                                    <div className="Yards-Header-Title-Lg">Address Street</div>
                                    <div className="Yards-Header-Title">City</div>
                                    <div className="Yards-Header-Title">Region</div>
                                    <div className="Yards-Header-Title">District</div>
                                    <div className="Yards-Header-Title-Sm">Zip</div>
                                    <div className="Yards-Header-Title-Lg">Action</div>
                                </div>
                                {yards.length > 0 && (
                                    <YardList
                                        yards={yards}
                                        regionsWithDistricts={regionsWithDistricts}
                                        isLoading={loading}
                                        canEditYards={canEditYard}
                                        canDeleteYards={canDeleteYard}
                                        onClickYard={openGoogleMaps}
                                        onEditClick={Yard => {
                                            setShowAddYardModal(true);
                                            setModalForm(ModalForm.EDIT_YARD);
                                            setSelectedYard(Yard);
                                        }}
                                        onDelete={onDelete}
                                        isEndOfResults={isEndOfResults}
                                        onLoadMore={() =>
                                            fetchMore({
                                                variables: {
                                                    offset,
                                                },
                                                updateQuery: (prev, { fetchMoreResult }) => {
                                                    if (
                                                        prev &&
                                                        fetchMoreResult &&
                                                        prev.getFilteredYards &&
                                                        fetchMoreResult.getFilteredYards
                                                    ) {
                                                        return Object.assign({}, prev, {
                                                            getFilteredYards: [
                                                                ...prev.getFilteredYards,
                                                                ...fetchMoreResult.getFilteredYards,
                                                            ],
                                                        });
                                                    } else {
                                                        return prev;
                                                    }
                                                },
                                            })
                                        }
                                    />
                                )}
                            </div>
                        )}

                        <AddEditYardsModal
                            show={showAddYardModal}
                            modalForm={modalForm}
                            options={yardTypes}
                            stateOptions={options}
                            regionsWithDistricts={regionsWithDistricts}
                            regions={regions}
                            selectedYard={selectedYard}
                            onClickHide={handleOnHide}
                            onClickSave={handleOnSave}
                            errorMessage={errorMessage}
                            saveLoading={saveLoading}
                        />
                    </div>
                );
            }}
        </Query>
    );
};

export default Yards;
